import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCmsCollection } from '../lib/cms.js';
import { imageUrl } from '../lib/images.js';

export default function CampaignBadge({ className = '' }) {
  const { data: campaignItems, loading } = useCmsCollection('forside_kampagne', []);

  // Determine active campaign item and deactivation status
  const { campaign, isDeactivated } = React.useMemo(() => {
    // If we have items from CMS
    if (campaignItems && campaignItems.length > 0) {
      const first = campaignItems[0];
      const data = first.data || first;

      // Strict check on aktiv boolean
      const isActive = data.aktiv === true || data.aktiv === 'true' || data.aktiv === 1;

      if (!isActive) {
        return { campaign: null, isDeactivated: true };
      }

      return { campaign: data, isDeactivated: false };
    }

    // If CMS has finished loading and collection is genuinely empty
    if (!loading && (!campaignItems || campaignItems.length === 0)) {
      return { campaign: null, isDeactivated: true };
    }

    // Default active fallback while initial fetch loads
    return {
      campaign: {
        aktiv: true,
        titel: 'Støt Claus og TFDI - Achensee Winter 100',
        badge_tag: 'AKTUELT',
        tekst_oeverst: 'Støt Claus og TFDI',
        tekst_nederst: 'Achensee Winter 100',
        billede: ['https://assets.auxodev.dk/assets/512cb13c-28f0-4ecb-9d9d-4ac03cf77200/image_a218565c.png'],
        link_url: '/stoet-os',
        link_eksternt: false,
        farvetema: 'blaa',
      },
      isDeactivated: false,
    };
  }, [campaignItems, loading]);

  // If explicitly disabled in CMS, render nothing at all
  if (isDeactivated || !campaign) {
    return null;
  }

  const {
    badge_tag = 'AKTUELT',
    tekst_oeverst = 'Støt Claus og TFDI',
    tekst_nederst = 'Achensee Winter 100',
    billede,
    link_url = '/stoet-os',
    link_eksternt = false,
    farvetema = 'blaa',
  } = campaign;

  // Image source resolution
  const rawImage = Array.isArray(billede) ? (billede[0] || '') : (billede || '');
  const fallbackImage = 'https://assets.auxodev.dk/assets/512cb13c-28f0-4ecb-9d9d-4ac03cf77200/image_a218565c.png';
  const imageSrc = (rawImage && typeof rawImage === 'string' && rawImage.trim().length > 0)
    ? rawImage.trim()
    : fallbackImage;

  // Normalized theme classes (supports Danish letters like blå, rød, mørk)
  const themeClasses = {
    blaa: {
      outerBg: 'bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-950',
      border: 'border-blue-300/40',
      ring: 'ring-blue-400/30',
      glow: 'rgba(37,99,235,0.45)',
      shadow: 'shadow-[0_12px_36px_rgba(30,58,138,0.55)]',
      textColor: 'fill-white',
      accentColor: 'fill-amber-300',
      tagBg: 'bg-red-600 text-white',
      innerBorder: 'border-white/90',
    },
    roed: {
      outerBg: 'bg-gradient-to-br from-red-600 via-red-700 to-rose-950',
      border: 'border-red-300/40',
      ring: 'ring-red-400/30',
      glow: 'rgba(220,38,38,0.45)',
      shadow: 'shadow-[0_12px_36px_rgba(159,18,57,0.55)]',
      textColor: 'fill-white',
      accentColor: 'fill-amber-300',
      tagBg: 'bg-amber-500 text-slate-900',
      innerBorder: 'border-white/90',
    },
    moerk: {
      outerBg: 'bg-gradient-to-br from-slate-800 via-slate-900 to-black',
      border: 'border-slate-500/40',
      ring: 'ring-slate-500/30',
      glow: 'rgba(0,0,0,0.6)',
      shadow: 'shadow-[0_12px_36px_rgba(0,0,0,0.7)]',
      textColor: 'fill-amber-300',
      accentColor: 'fill-white',
      tagBg: 'bg-blue-600 text-white',
      innerBorder: 'border-amber-300/80',
    },
    guld: {
      outerBg: 'bg-gradient-to-br from-amber-500 via-amber-600 to-yellow-900',
      border: 'border-amber-200/50',
      ring: 'ring-amber-300/30',
      glow: 'rgba(217,119,6,0.45)',
      shadow: 'shadow-[0_12px_36px_rgba(180,83,9,0.55)]',
      textColor: 'fill-white',
      accentColor: 'fill-amber-200',
      tagBg: 'bg-red-600 text-white',
      innerBorder: 'border-white/90',
    }
  };

  const normalizedThemeKey = (farvetema || '')
    .toLowerCase()
    .trim()
    .replace('å', 'aa')
    .replace('ø', 'oe')
    .replace('æ', 'ae');

  const themeKey = normalizedThemeKey === 'red' ? 'roed'
    : normalizedThemeKey === 'blue' ? 'blaa'
    : normalizedThemeKey === 'dark' ? 'moerk'
    : normalizedThemeKey === 'gold' ? 'guld'
    : normalizedThemeKey;

  const theme = themeClasses[themeKey] || themeClasses.blaa;

  const content = (
    <motion.div
      initial={{ opacity: 0, scale: 0.88, rotate: -3 }}
      animate={{ 
        opacity: 1, 
        scale: 1, 
        rotate: 0,
        y: [0, -5, 0] 
      }}
      transition={{ 
        opacity: { duration: 0.6, ease: 'easeOut' },
        scale: { duration: 0.6, ease: 'easeOut' },
        rotate: { duration: 0.6, ease: 'easeOut' },
        y: { duration: 4.5, repeat: Infinity, ease: 'easeInOut' }
      }}
      whileHover={{ scale: 1.05, rotate: 1 }}
      className="group relative select-none cursor-pointer"
      title={campaign.titel || 'Aktuel kampagne'}
    >
      {/* Outer ambient glow so the badge springs out from the hero background */}
      <div 
        className="absolute -inset-2 rounded-full blur-xl opacity-60 group-hover:opacity-90 transition-opacity duration-300 pointer-events-none -z-10"
        style={{ backgroundColor: theme.glow }}
      />

      {/* Top Tag / Pill - raised with clear separation so it never collides with top curved text */}
      {badge_tag && badge_tag.trim().length > 0 && (
        <div className="absolute -top-4 sm:-top-5 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
          <span className={`inline-flex items-center px-3.5 py-1 rounded-full text-[11px] sm:text-[12px] font-black uppercase tracking-widest shadow-xl ${theme.tagBg} border-2 border-white/90 whitespace-nowrap`}>
            <span>{badge_tag}</span>
          </span>
        </div>
      )}

      {/* Main Circular Container */}
      <div className={`relative w-48 h-48 sm:w-52 sm:h-52 md:w-56 md:h-56 rounded-full ${theme.outerBg} p-2 border-2 ${theme.border} ${theme.shadow} backdrop-blur-md transition-all duration-300 group-hover:border-white/70 group-hover:shadow-[0_16px_45px_rgba(37,99,235,0.65)]`}>
        
        {/* Subtle decorative dotted stitch ring (inspired by "vores nye hjemmeside" badge style) */}
        <div className="absolute inset-1.5 rounded-full border border-dashed border-white/35 pointer-events-none" />

        {/* SVG with circular paths for curved text around the border */}
        <svg
          viewBox="0 0 240 240"
          className="absolute inset-0 w-full h-full pointer-events-none z-10"
        >
          <defs>
            {/* Top arc (radius 94, exactly centered between inner disc r=80 and outer edge r=116) */}
            <path
              id="topCurvePath"
              d="M 26,120 A 94,94 0 0,1 214,120"
              fill="none"
            />
            {/* Bottom arc (radius 103, baseline centered so glyphs have equal padding to inner disc and outer edge) */}
            <path
              id="bottomCurvePath"
              d="M 17,120 A 103,103 0 0,0 223,120"
              fill="none"
            />
          </defs>

          {/* Top curved text with clear spacing and subtle drop shadow */}
          <text 
            className={`text-[13.5px] sm:text-[14.5px] font-black uppercase tracking-[0.09em] ${theme.textColor}`}
            style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }}
          >
            <textPath
              href="#topCurvePath"
              startOffset="50%"
              textAnchor="middle"
            >
              {tekst_oeverst}
            </textPath>
          </text>

          {/* Bottom curved text */}
          <text 
            className={`text-[13px] sm:text-[14px] font-black uppercase tracking-[0.12em] ${theme.textColor}`}
            style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }}
          >
            <textPath
              href="#bottomCurvePath"
              startOffset="50%"
              textAnchor="middle"
            >
              {tekst_nederst}
            </textPath>
          </text>
        </svg>

        {/* Center Disc with Logo/Image - generously sized and sharply framed */}
        <div className={`absolute inset-0 m-auto w-32 h-32 sm:w-36 sm:h-36 md:w-40 md:h-40 rounded-full bg-slate-900 shadow-xl flex items-center justify-center overflow-hidden border-2 ${theme.innerBorder} group-hover:scale-103 transition-transform duration-300 z-0`}>
          <img
            src={imageUrl(imageSrc, 'width=600,quality=90,format=auto')}
            alt={campaign.titel || 'Støt Claus og TFDI - Achensee Winter 100'}
            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
          />
          {/* Subtle inner ring shadow for depth */}
          <div className="absolute inset-0 rounded-full shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] pointer-events-none" />
        </div>

        {/* High-gloss shine highlight */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-white/10 to-white/20 pointer-events-none" />
      </div>
    </motion.div>
  );

  if (link_url) {
    const isExternal = link_eksternt === true || link_url.startsWith('http://') || link_url.startsWith('https://');
    if (isExternal) {
      return (
        <a
          href={link_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-block ${className}`}
        >
          {content}
        </a>
      );
    }
    return (
      <Link to={link_url} className={`inline-block ${className}`}>
        {content}
      </Link>
    );
  }

  return <div className={`inline-block ${className}`}>{content}</div>;
}
